import { getBFFApiClient } from '@acp/feature/auth';
import {
    AgencyKeyPickupLocation, APIResponse, EntryMethodPreferenceSettings, PropertyManager,
    PropertyManagerNotificationPreferences,
    NotificationPreferenceUpdate,
    AgencyPricingPackage,
    AddonService,
    AgencyPricingPackageSummary,
    PotentialIntegrationMatch,
    AddonBundleForm,
} from '@acp/shared';
import HttpStatusCodes from 'http-status-codes';

async function getPropertyManagers() {
    const response = await getBFFApiClient().get<APIResponse<PropertyManager[]>>('/v1/agency/property-managers');

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get property managers: ${response.data.error}`);
    }

    return response.data.data;
}

async function getAgencyKeyPickupLocations() {
    const response = await getBFFApiClient().get<APIResponse<AgencyKeyPickupLocation[]>>('/v1/agency/key-pickup-locations');

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get agency key pickup locations: ${response.data.error}`);
    }

    return response.data.data;
}

async function getAgencyEntryMethodPreference() {
    const response = await getBFFApiClient().get<APIResponse<EntryMethodPreferenceSettings>>('/v1/agency/entry-method-preferences');

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get agency entry method preferences');
    }

    return response.data.data;
}

async function getNotificationPreferences() {
    const response = await getBFFApiClient().get<APIResponse<PropertyManagerNotificationPreferences>>('/v1/property-manager/notification-settings');

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get property manager notification settings: ${response.data.error}`);
    }

    return response.data.data;
}

async function updatePropertyManagerNotificationPreferences(payload: NotificationPreferenceUpdate) {
    const response = await getBFFApiClient().post<APIResponse<null>>('/v1/property-manager/notification-settings', payload);

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to update property manager notification settings: ${response.data.error}`);
    }

    return response.data.data;
}

async function getAgencyPricingPackages() {
    const response = await getBFFApiClient().get<APIResponse<AgencyPricingPackage[]>>(
        '/v1/agency/pricing'
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get agency pricing packages: ${response.data.error}`);
    }

    return response.data.data;
}

async function getAgencyPricingPackage(pricingPackageId: number) {
    const response = await getBFFApiClient().get<APIResponse<AgencyPricingPackage>>(
        `/v1/agency/pricing/${pricingPackageId}`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get agency pricing package: ${response.data.error}`);
    }

    return response.data.data;
}

async function getAgencyPricingPackagePricingSummary(
    pricingPackageId: number,
    options: {
        pricingPackageTypeIds?: number[];
        oneOffServiceCodes?: string[];
        addonServices?: AddonService[];
        addonBundles?: AddonBundleForm[];
        propertyId?: number;
        productId?: number;
        smokeLastServiceDate?: string | null;
        gasLastServiceDate?: string | null;
        electricalLastServiceDate?: string | null;
        isCurrentEnrolment?: boolean;
    }
) {
    const response = await getBFFApiClient().post<APIResponse<AgencyPricingPackageSummary>>(
        `/v1/agency/pricing/${pricingPackageId}/summary`,
        options,
        {
            params: {
                isCurrentEnrolment: options.isCurrentEnrolment === true ? '1' : undefined,
            },
        }
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get agency pricing package summary: ${response.data.error}`);
    }

    return response.data.data;
}

async function getAgencyIntegrationMatches(address: string) {
    const response = await getBFFApiClient().get<APIResponse<PotentialIntegrationMatch[]>>('/v1/agency/integration/matches', {
        params: { address },
    });

    if (!response.data.success) {
        throw new Error(response.data.error ?? 'Failed to fetch integration matches');
    }

    return response.data.data;
}

export {
    getPropertyManagers,
    getAgencyKeyPickupLocations,
    getAgencyEntryMethodPreference,
    getNotificationPreferences,
    updatePropertyManagerNotificationPreferences,
    getAgencyPricingPackages,
    getAgencyPricingPackage,
    getAgencyPricingPackagePricingSummary,
    getAgencyIntegrationMatches,
};
